import LogService from "../services/LogService";
import IClass from "../types/Class";
import IEvent from "../types/Event";
import ILog from "../types/Log";
import IMasterClass from "../types/MasterClass";
import * as constantes from "../common/Constantes";
import ConfigUtils from "./Config";
import IActividad from "../types/Actividad";
import ISala from "../types/Sala";
import moment from "moment";

const formatClassEvent = (event: IEvent): IClass | undefined => {
    try {
        const formattedClass: IClass = {

            _id: event.id?.toString(),
            title: event.title,
            pacientesIds: event.resource.pacientesIds,
            especialistaId: event.resource.especialista,
            salaId: event.resource.sala,
            start: event.start,
            end: event.end,
            repId: event.resource.repId,
            userId: ConfigUtils.getUserId(),
            comentarios: event.resource.comentarios

        };
        return formattedClass;

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Clase.formatClassEvent', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatClaseDate = (event: IEvent | undefined) => {
    try {
        if (event === undefined) return;

        var d = new Date(event.start),
        dayWeek = '' + d.getDay(),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();

        const dayWeekIndex = (parseInt(dayWeek) + 6) % 7;
    
        return `${constantes.weekNames[dayWeekIndex]}, ${day} de ${constantes.monthNames[parseInt(month) - 1]} de ${year}`;
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Clase.formatClaseDate', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatClaseTime = (event: IEvent | undefined) => {
    try {
        if (event === undefined) return;

        var d = new Date(event.start),
        hoursStart = '' + (d.getHours()),
        minutesStart = '' + d.getMinutes();
    
        var d2 = new Date(new Date(event.end)),
        hoursEnd = '' + (d2.getHours()),
        minutesEnd = '' + d2.getMinutes();
    
        if (minutesStart.length < 2)
            minutesStart = '0' + minutesStart;
    
        if (minutesEnd.length < 2)
            minutesEnd = '0' + minutesEnd;
    
        return `de ${hoursStart}:${minutesStart} a ${hoursEnd}:${minutesEnd}`;
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Clase.formatClaseTime', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatClassTitle = (clase: IMasterClass) => {
    try {
        var d = new Date(clase.start),
        hoursStart = '' + (d.getHours()),
        minutesStart = '' + d.getMinutes();
        var d2 = new Date(new Date(clase.start).setMinutes(new Date(clase.start).getMinutes() + clase.duracion)),
        hoursEnd = '' + (d2.getHours()),
        minutesEnd = '' + d2.getMinutes();
    
        if (minutesStart.length < 2)
            minutesStart = '0' + minutesStart;
        if (minutesEnd.length < 2)
            minutesEnd = '0' + minutesEnd;
    
        // Especialista y sala para distinguir dos clases a la misma hora
        const especialista = ConfigUtils.getEmpleados().find(e => e.internalID === clase.especialistaId)?.firstName;
        const salas: ISala[] = ConfigUtils.getSalas();
        const sala = salas.find(s => String(s.internalID) === String(clase.salaId))?.title;
        const detalle = [especialista, sala].filter(Boolean).join(', ');
        // Fecha final de la serie: para saber hasta cuándo dura y no confundir series parecidas
        const hasta = clase.terminarRepetir ? ` · hasta ${moment(clase.terminarRepetir).format('DD/MM/YYYY')}` : '';

        return `${constantes.weekNames[clase.weekDay]}, de ${hoursStart}:${minutesStart} a ${hoursEnd}:${minutesEnd}${detalle ? ` · ${detalle}` : ''}${hasta}`;
    } catch (e: any) {
        const error: ILog = { method: 'Utils.Clase.formatClassTitle', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

// Clases creadas junto con esta (la «pareja», p. ej. martes y jueves a las 12): al crear una actividad para varios días se
// guarda una clase recurrente por día, todas con la misma actividad, inicio y fecha final. Si después se cambia la fecha
// final de solo una, dejan de ser pareja.
const parejasDe = (master: IMasterClass, masters: IMasterClass[]): IMasterClass[] =>
    masters.filter(m =>
        m._id !== master._id &&
        m.weekDay !== master.weekDay &&
        String(m.title) === String(master.title) &&
        new Date(m.start).getTime() === new Date(master.start).getTime() &&
        new Date(m.terminarRepetir).getTime() === new Date(master.terminarRepetir).getTime()
    ).sort((a, b) => a.weekDay - b.weekDay);

// Primera sesión real de una clase desde un día (incluido), o undefined si no hay ninguna. Las sesiones ya vienen sin
// festivos, así que es la fecha que se muestra como «empieza …» (el inicio guardado puede caer en festivo o en otro día).
const primeraSesion = (sesiones: Date[] | undefined, desde: Date): Date | undefined =>
    (sesiones ?? []).find(s => moment(s).isSameOrAfter(moment(desde), 'day'));

// Día de un tramo de paciente (se guardan como día UTC) como fecha local, para compararlo con las sesiones
const diaDeTramo = (fecha: Date): Date => {
    const dia = moment.utc(fecha);
    return new Date(dia.year(), dia.month(), dia.date());
};

// Actividad de la configuración del navegador por su código (el `title` de una clase o de una actividad programada);
// undefined si no está (actividad eliminada, o configuración del navegador desactualizada).
const actividadDe = (actividades: IActividad[], codigo: string | number | undefined | null): IActividad | undefined =>
    actividades.find(a => a.internalID?.toString() === String(codigo));

// Clases y actividades programadas cuya actividad no está en la configuración: no se muestran (para no romper el resto de la
// pantalla) y se registran en el log una vez por sesión del navegador (las pantallas se repintan a menudo).
type SinActividad = { id?: string | null; actividad: string; start?: Date };
const sinActividadRegistrados = new Set<string>();
const registrarSinActividad = (metodo: string, que: string, elementos: SinActividad[], actividades: IActividad[]) => {
    const nuevos = elementos.filter(e => !sinActividadRegistrados.has(String(e.id)));
    nuevos.forEach(e => sinActividadRegistrados.add(String(e.id)));
    if (nuevos.length === 0) return;
    const aviso: ILog = {
        method: metodo,
        level: 'warning',
        message: `${nuevos.length} ${que} con una actividad que no está en la configuración: no se muestran`,
        meta: { actividadesEnConfiguracion: actividades.map(a => a.internalID), elementos: nuevos.slice(0, 20) },
    };
    LogService.logError(aviso);
};

// Actividades programadas (series de clases) cuya actividad está en la configuración; el resto se omite y se registra.
const filtrarMastersConActividad = (masters: IMasterClass[], actividades: IActividad[], metodo: string): IMasterClass[] => {
    const sinActividad: SinActividad[] = [];
    const conActividad = masters.filter(m => {
        if (actividadDe(actividades, m.title)) return true;
        sinActividad.push({ id: m._id, actividad: String(m.title), start: m.start });
        return false;
    });
    registrarSinActividad(metodo, 'actividad(es) programada(s)', sinActividad, actividades);
    return conActividad;
};

const formatClasses = (data: IClass[], actividades: IActividad[]): IEvent[] => {
    try {
        const formattedClasses: IEvent[] = [];
        // Una clase cuya actividad no está en la configuración no se pinta (antes fallaba y desaparecían TODAS) y se registra.
        const sinActividad: SinActividad[] = [];

        data.forEach(clase => {
            const actividad = actividadDe(actividades, clase.title);
            if (!actividad) {
                sinActividad.push({ id: clase._id, actividad: String(clase.title), start: clase.start });
                return;
            }

            let formattedClass: IEvent = {
                id: clase._id,
                title: actividad.title,
                start: new Date(clase.start),
                end: clase.end ? new Date (clase.end) : new Date(),
                resource: { sala: clase.salaId, especialista: clase.especialistaId, pacientesIds: clase.pacientesIds, start: clase.start, end: clase.end, repId: clase.repId, comentarios: clase.comentarios, capacity: actividad.capacity },
            };
            formattedClasses.push(formattedClass);
        });

        registrarSinActividad('Utils.Clase.formatClasses', 'clase(s)', sinActividad, actividades);
        return formattedClasses;

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Clase.formatClasses', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return [];
    }
};

const formatClassesActividades = (data: IClass[], masters: IMasterClass[], actividades: IActividad[]): IEvent[] => {
    try {
        const formattedClasses: IEvent[] = [];

        const sinActividad: SinActividad[] = [];

        data.forEach(clase => {

            const master = masters.filter(m => clase.repId === m._id)[0];
            const actividad = master ? actividadDe(actividades, master.title) : undefined;
            // Clase de una actividad que no está en la configuración: no se pinta (el resto de la agenda sigue) y se registra
            if (master && !actividad) {
                sinActividad.push({ id: clase._id, actividad: String(master.title), start: clase.start });
                return;
            }

            // Pacientes distintos (un paciente con varios tramos cuenta una vez)
            const pacientesLength = master && master.pacientes !== undefined ? new Set(master.pacientes.filter(p => new Date(p.end).getTime() > new Date(clase.end).getTime() && new Date(p.start).getTime() < new Date(clase.start).getTime()).map(p => p.id)).size : 0;

            let formattedClass: IEvent = {
                id: clase._id,
                title: master ? `${actividad?.title} [${pacientesLength}/${actividad?.capacity}]` : 'CLASE ELIMINADA',
                start: new Date(clase.start),
                end: clase.end ? new Date (clase.end) : new Date(),
                resource: { sala: clase.salaId, especialista: clase.especialistaId, start: clase.start, end: clase.end, repId: clase.repId, ocupados: pacientesLength, capacidad: actividad?.capacity ?? 0 },
            };
            formattedClasses.push(formattedClass);
        });
        registrarSinActividad('Utils.Clase.formatClassesActividades', 'clase(s)', sinActividad, actividades);
        return formattedClasses;

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Clase.formatClassesActividades', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return [];
    }
};

const formatDate = (date: string) => {
    try {
        var d = new Date(date),
        month = '' + (d.getMonth() + 1),
        day = '' + d.getDate(),
        year = d.getFullYear();
    
        if (month.length < 2) 
            month = '0' + month;
        if (day.length < 2) 
            day = '0' + day;
    
        return [day, month, year].join('/');
    } catch (e: any) {
        const error: ILog = { method: 'ClaseUtils.formatDate', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatUTCDate = (date: Date) => {
    try {
        return moment.utc(date).format('DD/MM/YYYY');
    } catch (e: any) {
        const error: ILog = { method: 'ClaseUtils.formatUTCDate', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const formatUTCDateRange = (start: Date, end: Date) => {
    try {
        return `${moment.utc(start).format("DD/MM/YYYY")} - ${moment.utc(end).format("DD/MM/YYYY")}`;
    } catch (e: any) {
        const error: ILog = { method: 'ClaseUtils.formatUTCDateRange', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
    }
};

const ClaseUtils = {
    actividadDe,
    filtrarMastersConActividad,
    formatClassTitle,
    formatClassEvent,
    formatClaseDate,
    formatClaseTime,
    formatClasses,
    formatClassesActividades,
    formatDate,
    formatUTCDate,
    formatUTCDateRange,
    parejasDe,
    primeraSesion,
    diaDeTramo
};

export default ClaseUtils;